/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Radio } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import type { CountryLocaleInput } from '@sportninja/common/currency'
import { TEAM_BUDGET_PAYMENT_COPY as COPY } from '@sportninja/common/features/registration/teamBudgetPayment/constants/copy'
import type { TeamBudgetPaymentChoice as Choice } from '@sportninja/common/features/registration/teamBudgetPayment/types'
import { minimumPaymentCents } from '@sportninja/common/features/registration/teamBudgetPayment/utils/chosenPaymentAmount'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import CurrencyInput from 'src/components/ts-components/CurrencyInput'
import Text from 'src/components/ts-components/Text'

interface TeamBudgetPaymentChoiceProps {
  choice: Choice
  onChoiceChange: (choice: Choice) => void
  /** "Pay a different amount", in currency units, as last committed (blur); null while empty. */
  customAmount: number | null
  onCustomAmountChange: (amount: number) => void
  /** The amount as typed, before the field loses focus — what Enter submits. */
  onCustomAmountTyped: (amount: number | null) => void
  /** The server's answer on the amount (outside the range, below the card processor's minimum). */
  amountError: string | null
  /** The deposit (Minimum To Qualify), pre-tax, in cents. */
  depositCents: number
  /** The whole team fee, pre-tax, in cents — the most the rep may pay now. */
  teamFeeCents: number
  formatCents: (cents: number) => string
  /** The registration's currency country, so the amount field matches it. */
  country?: CountryLocaleInput
  disabled: boolean
}

const CUSTOM_AMOUNT_FIELD = 'team_budget_custom_amount'

/**
 * Sep 24 (Jeff + James) — on a Team Budget division the registering rep may pay
 * the deposit before finishing — or any other amount up to the whole team fee
 * (Inon, Sep 24) — or carry on without paying: the same card-and-radio shape as the
 * payment step's Defer Payment choice (PaymentMethodSelector), so the step reads as
 * one control group.
 */
const TeamBudgetPaymentChoice = ({
  choice,
  onChoiceChange,
  customAmount,
  onCustomAmountChange,
  onCustomAmountTyped,
  amountError,
  depositCents,
  teamFeeCents,
  formatCents,
  country,
  disabled,
}: TeamBudgetPaymentChoiceProps) => {
  const deposit = formatCents(depositCents)
  const minimumCents = minimumPaymentCents(depositCents)
  // A fee no larger than the least payable amount leaves no other amount to choose.
  const offersCustom = minimumCents < teamFeeCents

  const options: Array<{ value: Choice; title: string; detail: string }> = [
    {
      value: 'deposit',
      title: COPY.optionDeposit,
      detail: COPY.optionDepositDetail(deposit),
    },
    ...(offersCustom
      ? [
          {
            value: 'custom' as const,
            title: COPY.optionCustom,
            detail: COPY.optionCustomDetail(
              formatCents(minimumCents),
              formatCents(teamFeeCents)
            ),
          },
        ]
      : []),
    {
      value: 'later',
      title: COPY.optionLater,
      detail: COPY.optionLaterDetail,
    },
  ]

  return (
    <div css={containerStyle} data-testid='team-budget-payment-choice'>
      <Text variant='paragraphMedium' color={colors.NEUTRAL_0} weight='bold'>
        {capitalizeWords(COPY.choiceTitle)}
      </Text>
      <div css={separatorStyle} />
      <Text variant='paragraphSmall' weight='regular' color={colors.NEUTRAL_50}>
        {COPY.choiceIntro}
      </Text>
      <Radio.Group
        css={radioGroupStyle}
        value={choice}
        onChange={(event) => onChoiceChange(event.target.value as Choice)}
        disabled={disabled}
      >
        {options.map((option) => (
          <div key={option.value} css={optionStyle}>
            <Radio
              value={option.value}
              data-testid={`team-budget-choice-${option.value}`}
            >
              <div css={optionTextStyle}>
                <Text
                  variant='paragraphMedium'
                  weight='bold'
                  color={colors.WHITE}
                >
                  {capitalizeWords(option.title)}
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {option.detail}
                </Text>
              </div>
            </Radio>
            {option.value === 'custom' && choice === 'custom' ? (
              <div css={customFieldStyle}>
                <CurrencyInput
                  label={capitalizeWords(COPY.customFieldLabel)}
                  name={CUSTOM_AMOUNT_FIELD}
                  value={customAmount}
                  onChange={onCustomAmountChange}
                  onValueChange={onCustomAmountTyped}
                  min={0}
                  disabled={disabled}
                  country={country}
                  fieldErrors={amountError ? [CUSTOM_AMOUNT_FIELD] : []}
                  customError={
                    amountError ? { [CUSTOM_AMOUNT_FIELD]: amountError } : ''
                  }
                />
              </div>
            ) : null}
          </div>
        ))}
      </Radio.Group>
      {amountError && choice !== 'custom' ? (
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.ERROR_200}
        >
          {amountError}
        </Text>
      ) : null}
    </div>
  )
}

export default TeamBudgetPaymentChoice

const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.NEUTRAL_400};
  background: ${colors.NEUTRAL_600};
`

const separatorStyle = css`
  align-self: stretch;
  height: 0;
  border: 0.5px solid ${colors.NEUTRAL_400};
`

/** The Defer Payment radio's layout: the control on the right, text beside it. */
const radioGroupStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .ant-radio-wrapper {
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    width: 100%;
    margin: 0;
    padding: 0;

    > span:last-child {
      flex: 1;
      padding-inline-start: 0;
      padding-inline-end: 0;
    }
  }

  .ant-radio {
    display: flex;
    flex-shrink: 0;
  }

  .ant-radio-wrapper-disabled {
    cursor: not-allowed;
  }
`

const optionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const optionTextStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const customFieldStyle = css`
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: ${colors.NEUTRAL_700};
`
